/**
 * ## Home recovery score — how it works
 *
 * ### Two modes
 *
 * **1) Sleep-only (no Apple Health, or not connected)**  
 * Uses the same merged sleep as the rest of the app (`useSleepTracking` → manual
 * `sleep_logs` and/or HealthKit-backed hours when you are connected; if you are
 * not connected, only manual sleep counts).  
 * **Score** = `computeSleepRecoveryPercent` (duration vs 8h + quality 1–5, with
 * inferred quality from sleep stages when HealthKit provides them).  
 * Then multiply by a **workout fatigue factor** from the last **48 hours**
 * (total logged workout minutes — long blocks shave a few percent).
 *
 * **2) Extended (Apple Health connected)**  
 * Starts from the same **sleep** score, then — **only when data exists** — blends
 * in **HRV** (`hrv_sdnn_ms`) and **resting heart rate** (`resting_heart_rate_bpm`)
 * from `health_metrics` for **today’s local calendar row**, compared to your
 * **personal baseline** (median of the same metric on **prior** days in the
 * window; needs **≥ 4** baseline samples so we don’t react to noise).  
 * Weights among sleep / HRV / RHR are **renormalised** to 100% of whatever
 * components are available. If HRV or RHR is missing today, that leg is skipped.  
 * Same **workout fatigue** multiplier applies at the end.
 *
 * ### Workouts
 *
 * Completed workouts in the **last 48 hours** (by `completed_at`): we sum
 * `duration_minutes`. Light days leave recovery unchanged; stacked long sessions
 * apply a small downward multiplier so training load shows up in the number.
 *
 * ### Notes
 *
 * - Today’s HRV/RHR use **today’s** `metric_date` only (no carry-over from
 *   yesterday if today’s row is empty).
 * - Calendar keys use **local** `YYYY-MM-DD` (aligned with HealthKit sync).
 */

export type WorkoutForRecovery = {
  completed_at?: string | null;
  duration_minutes?: number | null;
};

export type HealthRecoveryMetricRow = {
  metric_date: string;
  hrv_sdnn_ms: number | null;
  resting_heart_rate_bpm: number | null;
};

/**
 * Sleep-only leg: 70% duration vs 8h target + 30% quality on a 1–5 scale.
 */
export function computeSleepRecoveryPercent(
  sleepHours: number,
  sleepQuality1to5: number
): number {
  const h = Math.max(0, sleepHours);
  const q = Math.min(5, Math.max(0, sleepQuality1to5));
  if (h <= 0) return 0;
  return Math.min(100, Math.round(((h / 8) * 0.7 + (q / 5) * 0.3) * 100));
}

/** Map restorative (deep + REM) share of total sleep to a 1–5 quality proxy. */
export function qualityFromSleepStages(
  totalHours: number,
  remHours?: number | null,
  deepHours?: number | null
): number | null {
  if (!(totalHours > 0)) return null;
  const rem = remHours != null && remHours > 0 ? remHours : 0;
  const deep = deepHours != null && deepHours > 0 ? deepHours : 0;
  const restorative = rem + deep;
  if (restorative <= 0) return null;
  const ratio = Math.min(1, restorative / totalHours);
  if (ratio >= 0.42) return 5;
  if (ratio >= 0.34) return 4;
  if (ratio >= 0.26) return 3;
  if (ratio >= 0.18) return 2;
  return 1;
}

function median(nums: number[]): number | null {
  const a = nums.filter((n) => Number.isFinite(n)).sort((x, y) => x - y);
  if (a.length === 0) return null;
  const mid = Math.floor(a.length / 2);
  return a.length % 2 === 1 ? a[mid]! : (a[mid - 1]! + a[mid]!) / 2;
}

/** Higher HRV vs your baseline → higher score (simplified population heuristic). */
function scoreHrvVsBaseline(today: number, baseline: number[]): number | null {
  if (!(today > 0) || baseline.length < 4) return null;
  const m = median(baseline);
  if (m == null || !(m > 0)) return null;
  const ratio = today / m;
  if (ratio >= 1.12) return 100;
  if (ratio >= 1.0) return Math.round(80 + ((ratio - 1.0) / 0.12) * 20);
  if (ratio >= 0.88) return Math.round(55 + ((ratio - 0.88) / 0.12) * 25);
  return Math.max(15, Math.round(55 * (ratio / 0.88)));
}

/** Lower resting HR vs your baseline → higher score. */
function scoreRhrVsBaseline(today: number, baseline: number[]): number | null {
  if (!(today > 0) || baseline.length < 4) return null;
  const m = median(baseline);
  if (m == null || !(m > 0)) return null;
  const ratio = today / m;
  if (ratio <= 0.96) return 100;
  if (ratio <= 1.0) return Math.round(88 + ((1.0 - ratio) / 0.04) * 12);
  if (ratio <= 1.06) return Math.round(70 + ((1.06 - ratio) / 0.06) * 18);
  return Math.max(20, Math.round(70 - (ratio - 1.06) * 120));
}

/**
 * Fatigue from recent training: sum `duration_minutes` in the last 48h.
 * Returns a multiplier in (0.85, 1] applied to the blended recovery.
 */
export function computeWorkoutFatigueMultiplier(
  workouts: WorkoutForRecovery[],
  nowMs: number = Date.now()
): number {
  const cutoff = nowMs - 48 * 60 * 60 * 1000;
  let totalMin = 0;
  for (const w of workouts) {
    if (!w.completed_at) continue;
    const t = new Date(w.completed_at).getTime();
    if (t >= cutoff && t <= nowMs) totalMin += Math.max(0, w.duration_minutes ?? 0);
  }
  if (totalMin <= 90) return 1;
  if (totalMin <= 180) return 0.97;
  if (totalMin <= 300) return 0.93;
  return 0.88;
}

export function computeCompositeRecoveryPercent(params: {
  sleepHours: number;
  sleepQuality1to5: number;
  appleHealthConnected: boolean;
  todayLocalKey: string;
  metricRows: HealthRecoveryMetricRow[];
  workouts: WorkoutForRecovery[];
  nowMs?: number;
}): number {
  const {
    sleepHours,
    sleepQuality1to5,
    appleHealthConnected,
    todayLocalKey,
    metricRows,
    workouts,
    nowMs = Date.now(),
  } = params;

  const sleepScore =
    sleepHours > 0 ? computeSleepRecoveryPercent(sleepHours, sleepQuality1to5) : 0;
  const fatigue = computeWorkoutFatigueMultiplier(workouts, nowMs);

  if (!appleHealthConnected || sleepHours <= 0) {
    return Math.round(Math.min(100, Math.max(0, sleepScore * fatigue)));
  }

  const pastRows = metricRows.filter((r) => r.metric_date < todayLocalKey);
  const todayRow = metricRows.find((r) => r.metric_date === todayLocalKey);

  const hrvBaseline = pastRows
    .map((r) => r.hrv_sdnn_ms)
    .filter((n): n is number => n != null && n > 0);
  const rhrBaseline = pastRows
    .map((r) => r.resting_heart_rate_bpm)
    .filter((n): n is number => n != null && n > 0);

  const todayHrv = todayRow?.hrv_sdnn_ms;
  const todayRhr = todayRow?.resting_heart_rate_bpm;

  const hrvScore =
    todayHrv != null && todayHrv > 0 ? scoreHrvVsBaseline(todayHrv, hrvBaseline) : null;
  const rhrScore =
    todayRhr != null && todayRhr > 0 ? scoreRhrVsBaseline(todayRhr, rhrBaseline) : null;

  let wSleep = 1;
  let wHrv = 0;
  let wRhr = 0;
  if (hrvScore != null && rhrScore != null) {
    wSleep = 0.5;
    wHrv = 0.28;
    wRhr = 0.22;
  } else if (hrvScore != null) {
    wSleep = 0.62;
    wHrv = 0.38;
  } else if (rhrScore != null) {
    wSleep = 0.62;
    wRhr = 0.38;
  }

  const parts: number[] = [];
  const weights: number[] = [];
  if (wSleep > 0) {
    parts.push(sleepScore);
    weights.push(wSleep);
  }
  if (hrvScore != null && wHrv > 0) {
    parts.push(hrvScore);
    weights.push(wHrv);
  }
  if (rhrScore != null && wRhr > 0) {
    parts.push(rhrScore);
    weights.push(wRhr);
  }

  const wsum = weights.reduce((a, b) => a + b, 0);
  const blended = parts.map((p, i) => p * weights[i]!).reduce((a, b) => a + b, 0) / wsum;
  return Math.round(Math.min(100, Math.max(0, blended * fatigue)));
}
